import { memo } from "react";

type MaintenancePageProps = {
  title?: string;
  message?: string;
  eta?: string;
  contactEmail?: string;
};

function getEnv(name: string): string | undefined {
  // vite.config.ts maps runtime env into process.env for this project

  return typeof process !== "undefined"
    ? (process.env?.[name] as any)
    : undefined;
}

const MaintenancePage = memo(
  ({ title, message, eta, contactEmail }: MaintenancePageProps) => {
    const fallbackTitle =
      title ?? getEnv("MAINTENANCE_TITLE") ?? "Maintenance en cours";
    const fallbackMessage =
      message ??
      getEnv("MAINTENANCE_MESSAGE") ??
      "BATAIL-LOG est temporairement indisponible pour cause de maintenance.";
    const fallbackEta = eta ?? getEnv("MAINTENANCE_ETA");
    const fallbackContact = contactEmail ?? getEnv("MAINTENANCE_CONTACT_EMAIL");

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "radial-gradient(1200px 600px at 20% 10%, rgba(69,125,217,0.30), rgba(0,0,0,0) 60%), radial-gradient(1200px 600px at 80% 90%, rgba(69,125,217,0.18), rgba(0,0,0,0) 60%), #0b0f1a",
          color: "#eef2ff",
          padding: 24,
          boxSizing: "border-box",
        }}
      >
        <main
          style={{
            width: "min(720px, 100%)",
            borderRadius: 18,
            padding: 28,
            boxSizing: "border-box",
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.12)",
            backdropFilter: "blur(10px)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                flexWrap: "wrap",
              }}
            >
              <img
                src="/logo.png"
                alt="BATAIL-LOG"
                style={{
                  width: 54,
                  height: 54,
                  objectFit: "contain",
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(69,125,217,0.30)",
                }}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div
                  style={{
                    fontSize: 12,
                    letterSpacing: 1.6,
                    opacity: 0.9,
                    textTransform: "uppercase",
                    color: "#c7d2fe",
                  }}
                >
                  BATAIL-LOG
                </div>
                <h1 style={{ margin: 0, fontSize: 28, lineHeight: 1.2 }}>
                  {fallbackTitle}
                </h1>
              </div>
            </div>
            <div
              aria-hidden="true"
              style={{
                width: 12,
                height: 12,
                borderRadius: 999,
                background: "#457DD9",
                boxShadow: "0 0 0 6px rgba(69,125,217,0.22)",
                flex: "0 0 auto",
              }}
              title="Maintenance"
            />
          </div>

          <p style={{ margin: "16px 0 0", fontSize: 16, opacity: 0.95 }}>
            {fallbackMessage}
          </p>

          {fallbackEta ? (
            <p style={{ margin: "10px 0 0", fontSize: 14, opacity: 0.85 }}>
              Retour estimé: <strong>{fallbackEta}</strong>
            </p>
          ) : null}

          {fallbackContact ? (
            <p style={{ margin: "10px 0 0", fontSize: 14, opacity: 0.85 }}>
              Contact:{" "}
              <a
                href={`mailto:${fallbackContact}`}
                style={{ color: "#c7d2fe" }}
              >
                {fallbackContact}
              </a>
            </p>
          ) : null}

          <div
            style={{
              marginTop: 18,
              paddingTop: 18,
              borderTop: "1px solid rgba(255,255,255,0.12)",
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
              opacity: 0.9,
              fontSize: 13,
            }}
          >
            <span>Merci pour votre patience.</span>
          </div>
        </main>
      </div>
    );
  }
);

MaintenancePage.displayName = "MaintenancePage";

export default MaintenancePage;
